import type {
    IrrigationScheduleConfig,
    IrrigationSession,
} from '@/lib/api/irrigation-devices-api'
import { m } from '@/lib/paraglide/messages'

// Chỉ để HIỂN THỊ lịch tưới. Quy tắc (validate, sinh phiên) nằm ở backend
// (irrigationschedule); xem trước gọi API chứ không tính ở đây.

const TZ = 'Asia/Ho_Chi_Minh'

/** 1 = Thứ 2 … 7 = Chủ nhật, cùng quy ước với backend. */
export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const

export function weekdayLabel(day: number): string {
    const labels: Record<number, () => string> = {
        1: m.irrigation_weekday_1,
        2: m.irrigation_weekday_2,
        3: m.irrigation_weekday_3,
        4: m.irrigation_weekday_4,
        5: m.irrigation_weekday_5,
        6: m.irrigation_weekday_6,
        7: m.irrigation_weekday_7,
    }
    return labels[day]?.() ?? String(day)
}

/** "Hằng ngày" khi đủ 7 ngày, còn lại "T2, T4, T6". */
export function weekdaysLabel(days: ReadonlyArray<number>): string {
    const sorted = [...new Set(days)].sort((a, b) => a - b)
    if (sorted.length === 7) return m.irrigation_schedule_every_day()
    return sorted.map(weekdayLabel).join(', ')
}

interface VnParts {
    hh: string
    mi: string
    dd: string
    mm: string
    /** 1 = Thứ 2 … 7 = Chủ nhật. */
    weekday: number
}

const vnFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
    weekday: 'short',
    hourCycle: 'h23',
})

const WEEKDAY_INDEX: Record<string, number> = {
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
    Sun: 7,
}

/** Tách giờ Việt Nam thành từng phần; tự ghép chuỗi để không phụ thuộc cách
 *  mỗi trình duyệt/ICU định dạng locale vi-VN ("28/09" hay "28-09"). */
function vnParts(d: Date): VnParts {
    const get = (type: Intl.DateTimeFormatPartTypes) =>
        vnFormatter.formatToParts(d).find((p) => p.type === type)?.value ?? ''
    return {
        hh: get('hour'),
        mi: get('minute'),
        dd: get('day'),
        mm: get('month'),
        weekday: WEEKDAY_INDEX[get('weekday')] ?? 0,
    }
}

/** "06:00" hoặc "06:00 28/09" theo giờ Việt Nam. */
export function formatTimeVN(iso: string, withDate = false): string {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    const p = vnParts(d)
    const time = `${p.hh}:${p.mi}`
    return withDate ? `${time} ${p.dd}/${p.mm}` : time
}

/** Câu tóm tắt một lịch, ví dụ "T2, T4, lúc 06:00, 17:00, mỗi lần 15 phút". */
export function scheduleSummary(config: IrrigationScheduleConfig): string {
    const days = weekdaysLabel(config.weekdays)
    if (config.mode === 'interval') {
        return m.irrigation_schedule_summary_interval({
            days,
            from: config.window_start ?? '',
            to: config.window_end ?? '',
            every: String(config.every_minutes ?? 0),
            minutes: String(config.duration_minutes),
        })
    }
    return m.irrigation_schedule_summary_fixed({
        days,
        times: [...(config.start_times ?? [])].sort().join(', '),
        minutes: String(config.duration_minutes),
    })
}

export interface SessionDay {
    /** Nhãn ngày, ví dụ "T2 28/09". */
    label: string
    sessions: Array<IrrigationSession>
}

/** Gom phiên theo ngày (giờ Việt Nam) để xem trước. */
export function groupSessionsByDay(
    sessions: ReadonlyArray<IrrigationSession>,
): Array<SessionDay> {
    const days: Array<SessionDay> = []
    for (const s of sessions) {
        const p = vnParts(new Date(s.start))
        const label = `${weekdayLabel(p.weekday)} ${p.dd}/${p.mm}`
        const last = days.at(-1)
        if (last && last.label === label) {
            last.sessions.push(s)
        } else {
            days.push({ label, sessions: [s] })
        }
    }
    return days
}

/**
 * Lỗi backend có `details.field` (vd IRRIGATION_SCHEDULE_OVERLAP →
 * start_times): trả tên ô để form gắn câu lỗi đã dịch vào đúng chỗ.
 */
export function fieldOfApiError(err: unknown): string | null {
    if (
        typeof err === 'object' &&
        err !== null &&
        'details' in err &&
        typeof (err as { details?: unknown }).details === 'object'
    ) {
        const field = (err as { details?: { field?: unknown } }).details?.field
        return typeof field === 'string' && field ? field : null
    }
    return null
}
